import { Building04, CheckVerified01, Hash01, User01 } from "@untitledui/icons";
import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "@/utils/cx";
import { TableBodyCell } from "./table-body-cell";
import { TableCellContent, TableCellContentType } from "./table-cell-content";
import {
	TableSelectionCellRow,
	TableSelectionCellRowState,
} from "./table-selection-cell-row";

export const TableBodyRowState = {
	Default: "default",
	Alternate: "alternate",
	Hover: "hover",
	Selected: "selected",
	Focused: "focused",
	Disabled: "disabled",
} as const;

export type TableBodyRowState =
	(typeof TableBodyRowState)[keyof typeof TableBodyRowState];

export interface TableBodyRowProps
	extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
	/**
	 * Show the leading row-selection checkbox column (Figma `selectable`).
	 * On by default.
	 */
	selectable?: boolean;
	/**
	 * Interaction / zebra state for design-system previews (Figma `state`).
	 * In live tables, hover/selected/focus/disabled come from React Aria;
	 * alternate (zebra) is applied via `CustomTableWrapper` `zebraStriping`.
	 */
	state?: TableBodyRowState;
	/**
	 * Body cell content. When omitted, renders a Figma-style demo set of
	 * {@link TableBodyCell} + {@link TableCellContent} for Storybook.
	 */
	children?: ReactNode;
}

/** Destiny BodyRow state fills — shared with live `CustomTable.Row`. */
export const tableBodyRowStateClassName: Record<TableBodyRowState, string> = {
	default: "bg-primary",
	alternate: "bg-secondary",
	hover: "bg-[rgba(181,71,8,0.05)]",
	selected: "bg-[rgba(181,71,8,0.1)]",
	focused:
		"bg-primary shadow-[0_0_0_2px_var(--color-bg-primary),0_0_0_4px_var(--color-focus-ring)]",
	disabled: "bg-primary opacity-60",
};

/**
 * Interactive BodyRow chrome for Aria `tr` (hover / selected / focused / disabled).
 * Zebra striping is applied by the shell via row index / style overrides.
 * Keep hover/focus utilities as full literals so Tailwind can see them.
 */
export const tableBodyRowInteractiveClassName =
	"relative h-[50px] bg-primary outline-focus-ring transition-colors after:pointer-events-none hover:bg-[rgba(181,71,8,0.05)] focus-visible:shadow-[0_0_0_2px_var(--color-bg-primary),0_0_0_4px_var(--color-focus-ring)] focus-visible:outline-none disabled:opacity-60";

/** Map React Aria row render state → Destiny {@link TableBodyRowState}. */
export function tableBodyRowStateFromAria(state: {
	isSelected?: boolean;
	isDisabled?: boolean;
	isFocusVisible?: boolean;
	isHovered?: boolean;
}): TableBodyRowState {
	if (state.isDisabled) return TableBodyRowState.Disabled;
	if (state.isSelected) return TableBodyRowState.Selected;
	if (state.isFocusVisible) return TableBodyRowState.Focused;
	if (state.isHovered) return TableBodyRowState.Hover;
	return TableBodyRowState.Default;
}

/**
 * Default demo cells matching Figma Table/BodyRow sample content
 * (Web-DesignSystem-2026 / 3049:539).
 */
function DefaultBodyRowCells() {
	return (
		<>
			<TableBodyCell className="w-auto shrink-0">
				<TableCellContent
					type={TableCellContentType.FeaturedIcon}
					icon={CheckVerified01}
				/>
			</TableBodyCell>
			<TableBodyCell className="w-auto shrink-0">
				<TableCellContent
					type={TableCellContentType.Label}
					primary="101"
					icon={Hash01}
				/>
			</TableBodyCell>
			<TableBodyCell className="w-[200px] shrink-0">
				<TableCellContent
					type={TableCellContentType.Primary}
					primary="Fiber Installation Riverside Dr N"
				/>
			</TableBodyCell>
			<TableBodyCell className="w-[215px] shrink-0">
				<TableCellContent
					type={TableCellContentType.Secondary}
					primary="Installing fiber on one side of the building for this business."
				/>
			</TableBodyCell>
			<TableBodyCell className="w-auto shrink-0">
				<TableCellContent
					type={TableCellContentType.Badge}
					primary="Badge"
					badgeColor="blue"
				/>
			</TableBodyCell>
			<TableBodyCell className="w-auto shrink-0">
				<TableCellContent
					type={TableCellContentType.Label}
					primary="Label"
					icon={Building04}
				/>
			</TableBodyCell>
			<TableBodyCell className="w-auto shrink-0">
				<TableCellContent
					type={TableCellContentType.Badge}
					primary="Badge"
					badgeColor="blue"
					badgeType="pill-color"
				/>
			</TableBodyCell>
			<TableBodyCell className="w-auto shrink-0">
				<TableCellContent
					type={TableCellContentType.Label}
					primary="Label"
					icon={Building04}
				/>
			</TableBodyCell>
			<TableBodyCell className="w-auto shrink-0">
				<TableCellContent type={TableCellContentType.Secondary} primary="8" />
			</TableBodyCell>
			<TableBodyCell className="w-auto shrink-0">
				<TableCellContent type={TableCellContentType.Secondary} primary="2" />
			</TableBodyCell>
			<TableBodyCell className="w-auto shrink-0">
				<TableCellContent
					type={TableCellContentType.Label}
					primary="Label"
					icon={User01}
				/>
			</TableBodyCell>
			<TableBodyCell className="w-auto shrink-0">
				<TableCellContent
					type={TableCellContentType.Secondary}
					primary="Timothy Anderson"
				/>
			</TableBodyCell>
			<TableBodyCell className="w-auto shrink-0">
				<TableCellContent
					type={TableCellContentType.Date}
					primary="May 7, 2026"
				/>
			</TableBodyCell>
			<TableBodyCell className="w-auto shrink-0">
				<TableCellContent
					type={TableCellContentType.Date}
					primary="May 7, 2026"
				/>
			</TableBodyCell>
			<TableBodyCell className="w-auto shrink-0">
				<TableCellContent
					type={TableCellContentType.Date}
					primary="May 7, 2026"
				/>
			</TableBodyCell>
			<TableBodyCell className="w-auto shrink-0">
				<TableCellContent
					type={TableCellContentType.Secondary}
					primary="Cell value"
				/>
			</TableBodyCell>
		</>
	);
}

/**
 * Presentational table body row matching Figma Table/BodyRow (3049:539).
 * Live React Aria tables use `CustomTable.Row` for the same chrome; use this
 * shell outside Aria / in demos. Pass `children` for custom cell composition.
 */
export function TableBodyRow({
	selectable = true,
	state = TableBodyRowState.Default,
	children,
	className,
	...props
}: TableBodyRowProps) {
	const selectionState =
		state === TableBodyRowState.Selected
			? TableSelectionCellRowState.Checked
			: TableSelectionCellRowState.Unchecked;

	return (
		<div
			{...props}
			data-selectable={selectable}
			data-state={state}
			data-slot="table-body-row"
			className={cx(
				"relative flex w-full flex-col items-stretch overflow-clip",
				className,
			)}
		>
			<div
				className={cx(
					// Figma BodyRow — h-50 row of cells; state drives bg / focus / opacity
					"relative flex h-[50px] items-stretch overflow-clip",
					tableBodyRowStateClassName[state],
				)}
			>
				{selectable ? <TableSelectionCellRow state={selectionState} /> : null}
				{children ?? <DefaultBodyRowCells />}
			</div>
		</div>
	);
}
